import { useEffect, useId, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@ludus/ui'
import type { AuditsApiError } from '../api/index.ts'
import { useAuditPipelines, useCreateAudit } from '../hooks/index.ts'
import { AUDIT_STAGES } from '../types.ts'
import { dateFromInput } from '../utils/format.ts'
import { CREATE_AUDIT_DEFAULTS, createAuditSchema, type CreateAuditFormValues } from './schema.ts'

interface CreateAuditWizardProps {
  open: boolean
  onOpenChange: (next: boolean) => void
}

/**
 * The creation wizard (§10.1, #240): pipelines, period, cutoff, the stage
 * set, the quota and the provider overrides. It warns when no deal pipeline
 * is chosen (every cycle will be call-only) and when no interaction pipeline
 * is (the call stages will skip). An uncovered period does not fail: the
 * audit is created as a draft and its page shows the backfill.
 */
export function CreateAuditWizard({ open, onOpenChange }: CreateAuditWizardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const formId = useId()
  const pipelines = useAuditPipelines()
  const { trigger, isMutating } = useCreateAudit()
  const [submitError, setSubmitError] = useState<AuditsApiError | null>(null)

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateAuditFormValues>({
    resolver: zodResolver(createAuditSchema),
    defaultValues: CREATE_AUDIT_DEFAULTS,
  })

  useEffect(() => {
    if (open) {
      reset(CREATE_AUDIT_DEFAULTS)
      setSubmitError(null)
    }
  }, [open, reset])

  const chosen = watch('pipelineIds')
  const kinds = new Set(pipelines.filter((p) => chosen.includes(p.id)).map((p) => p.kind))
  const noDeal = chosen.length > 0 && !kinds.has('deal')
  const noInteraction = chosen.length > 0 && !kinds.has('interaction')

  const onSubmit = handleSubmit(async (values) => {
    if (isMutating) return
    setSubmitError(null)
    try {
      const created = await trigger({
        title: values.title,
        pipelineIds: values.pipelineIds,
        periodFrom: dateFromInput(values.periodFrom)!,
        periodTo: dateFromInput(values.periodTo)!,
        ...(values.cutoffAt === '' ? {} : { cutoffAt: dateFromInput(values.cutoffAt)! }),
        stages: values.stages,
        quota: {
          asr_minutes: values.asrMinutes,
          llm_tokens: values.llmTokens,
          amount: values.amount,
          currency: values.currency,
        },
        providers: {
          ...(values.asr === '' ? {} : { asr: values.asr }),
          ...(values.llm === '' ? {} : { llm: values.llm }),
          ...(values.llmModel === '' ? {} : { llm_model: values.llmModel }),
        },
      })
      onOpenChange(false)
      if (created) navigate(`/audits/${created.id}`)
    } catch (err) {
      setSubmitError(err as AuditsApiError)
    }
  })

  const error = (key: keyof CreateAuditFormValues) => {
    const message = errors[key]?.message
    return typeof message === 'string'
      ? t(`pages.audits.wizard.errors.${message}`, { defaultValue: message })
      : null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('pages.audits.wizard.title', { defaultValue: 'New audit' })}</DialogTitle>
          <DialogDescription>
            {t('pages.audits.wizard.description', {
              defaultValue:
                'Freeze a period of the mirror and run the stages over it. An uncovered period stays a draft until its backfill finishes.',
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-5 text-sm">
            <Field
              label={t('pages.audits.wizard.fields.title', { defaultValue: 'Title' })}
              error={error('title')}
            >
              <Input {...register('title')} placeholder="July 2026" />
            </Field>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted">
                {t('pages.audits.wizard.fields.pipelines', { defaultValue: 'Pipelines' })}
              </legend>
              <Controller
                control={control}
                name="pipelineIds"
                render={({ field }) => (
                  <ul className="flex flex-col gap-1">
                    {pipelines.map((pipeline) => {
                      const checked = field.value.includes(pipeline.id)
                      return (
                        <li key={pipeline.id}>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, pipeline.id]
                                    : field.value.filter((id) => id !== pipeline.id),
                                )
                              }
                            />
                            <span>{pipeline.name}</span>
                            <Badge tone="muted" size="sm">
                              {t(`pages.audits.wizard.kind.${pipeline.kind}`, {
                                defaultValue: pipeline.kind,
                              })}
                            </Badge>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                )}
              />
              {error('pipelineIds') && (
                <p className="text-xs text-toast-error-text">{error('pipelineIds')}</p>
              )}
              {noDeal && (
                <p role="status" className="text-xs text-toast-warning-text">
                  {t('pages.audits.wizard.warnings.noDeal', {
                    defaultValue: 'No deal pipeline is selected: every cycle will be call-only.',
                  })}
                </p>
              )}
              {noInteraction && (
                <p role="status" className="text-xs text-toast-warning-text">
                  {t('pages.audits.wizard.warnings.noInteraction', {
                    defaultValue: 'No interaction pipeline is selected: the call stages will skip.',
                  })}
                </p>
              )}
            </fieldset>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field
                label={t('pages.audits.wizard.fields.periodFrom', { defaultValue: 'Period from' })}
                error={error('periodFrom')}
              >
                <Input type="date" {...register('periodFrom')} />
              </Field>
              <Field
                label={t('pages.audits.wizard.fields.periodTo', { defaultValue: 'Period to' })}
                error={error('periodTo')}
              >
                <Input type="date" {...register('periodTo')} />
              </Field>
              <Field
                label={t('pages.audits.wizard.fields.cutoff', { defaultValue: 'Cutoff' })}
                hint={t('pages.audits.wizard.hints.cutoff', {
                  defaultValue: 'Defaults to the period end.',
                })}
                error={error('cutoffAt')}
              >
                <Input type="date" {...register('cutoffAt')} />
              </Field>
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted">
                {t('pages.audits.wizard.fields.stages', { defaultValue: 'Stages' })}
              </legend>
              <Controller
                control={control}
                name="stages"
                render={({ field }) => (
                  <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3">
                    {AUDIT_STAGES.map((stage) => {
                      const checked = field.value.includes(stage)
                      return (
                        <li key={stage}>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={stage === 'snapshot'}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.checked
                                    ? AUDIT_STAGES.filter(
                                        (s) => s === stage || field.value.includes(s),
                                      )
                                    : field.value.filter((s) => s !== stage),
                                )
                              }
                            />
                            <span>
                              {t(`pages.audits.stages.${stage}`, { defaultValue: stage })}
                            </span>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                )}
              />
              {error('stages') && (
                <p className="text-xs text-toast-error-text">{error('stages')}</p>
              )}
            </fieldset>

            <fieldset className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <legend className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted">
                {t('pages.audits.wizard.fields.quota', { defaultValue: 'Quota' })}
              </legend>
              <Field
                label={t('pages.audits.wizard.fields.asrMinutes', { defaultValue: 'ASR minutes' })}
                error={error('asrMinutes')}
              >
                <Input type="number" min={0} {...register('asrMinutes')} />
              </Field>
              <Field
                label={t('pages.audits.wizard.fields.llmTokens', { defaultValue: 'LLM tokens' })}
                error={error('llmTokens')}
              >
                <Input type="number" min={0} {...register('llmTokens')} />
              </Field>
              <Field
                label={t('pages.audits.wizard.fields.amount', {
                  defaultValue: 'Amount (minor units)',
                })}
                error={error('amount')}
              >
                <Input type="number" min={0} {...register('amount')} />
              </Field>
              <Field
                label={t('pages.audits.wizard.fields.currency', { defaultValue: 'Currency' })}
                error={error('currency')}
              >
                <Input {...register('currency')} />
              </Field>
            </fieldset>

            <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <legend className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted">
                {t('pages.audits.wizard.fields.providers', { defaultValue: 'Provider overrides' })}
              </legend>
              <Field label={t('pages.audits.wizard.fields.asr', { defaultValue: 'ASR provider' })}>
                <Input {...register('asr')} placeholder="runpod-whisperx" />
              </Field>
              <Field label={t('pages.audits.wizard.fields.llm', { defaultValue: 'LLM provider' })}>
                <Input {...register('llm')} placeholder="anthropic" />
              </Field>
              <Field
                label={t('pages.audits.wizard.fields.llmModel', { defaultValue: 'LLM model' })}
              >
                <Input {...register('llmModel')} placeholder="claude-sonnet-5" />
              </Field>
            </fieldset>

            {submitError && (
              <p role="alert" className="text-xs text-toast-error-text">
                {t('pages.audits.wizard.errors.submit', {
                  defaultValue: 'The engine refused the audit: {{message}}',
                  message: submitError.message,
                })}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <DialogClose>
            <Button variant="ghost" size="sm" type="button">
              {t('pages.audits.wizard.cancel', { defaultValue: 'Cancel' })}
            </Button>
          </DialogClose>
          <Button variant="primary" size="sm" type="submit" form={formId} disabled={isMutating}>
            {t('pages.audits.wizard.submit', { defaultValue: 'Create audit' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string | null
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted">{label}</span>
      {children}
      {hint && !error && <span className="text-[11px] text-muted">{hint}</span>}
      {error && <span className="text-[11px] text-toast-error-text">{error}</span>}
    </label>
  )
}
